import React from "react";
import {
  MdOndemandVideo,
  MdImage,
  MdInsertDriveFile,
  MdQuiz,
  MdDragIndicator,
  MdMoreVert,
  MdEdit,
  MdLock,
  MdLockOpen,
  MdLink,
  MdLinkOff,
} from "react-icons/md";
import type { FolderItem, FolderItemType } from "services/instructorFolderItemsService";
import InlineRename from "components/ui/InlineRename";

const ICONS: Record<FolderItemType, React.ReactNode> = {
  video: <MdOndemandVideo className="h-5 w-5" />,
  image: <MdImage className="h-5 w-5" />,
  file: <MdInsertDriveFile className="h-5 w-5" />,
  quiz: <MdQuiz className="h-5 w-5" />,
};

const TONES: Record<FolderItemType, string> = {
  video: "bg-purple-50 text-purple-500 dark:bg-purple-500/10 dark:text-purple-200",
  image: "bg-amber-50 text-amber-500 dark:bg-amber-500/10 dark:text-amber-200",
  file: "bg-sky-50 text-sky-500 dark:bg-sky-500/10 dark:text-sky-200",
  quiz: "bg-indigo-50 text-indigo-500 dark:bg-indigo-500/10 dark:text-indigo-200",
};

type Props = {
  item: FolderItem;
  onRename: (item: FolderItem, nextTitle: string) => Promise<void> | void;
  onOpenMenu: (item: FolderItem, anchor: HTMLElement) => void;
  onToggleAccess: (item: FolderItem) => Promise<void> | void;
  onToggleRequiresPrevious: (item: FolderItem) => Promise<void> | void;
  onOpen?: (item: FolderItem) => void;
  previewUrl?: string | null;
  dragHandleProps?: React.HTMLAttributes<HTMLButtonElement>;
  dragging?: boolean;
};

export default function ItemCard({
  item,
  onRename,
  onOpenMenu,
  onToggleAccess,
  onToggleRequiresPrevious,
  onOpen,
  previewUrl,
  dragHandleProps,
  dragging,
}: Props) {
  const [thumbBroken, setThumbBroken] = React.useState(false);
  const isLocked = item.is_locked !== false;
  const requiresPrevious = Boolean(item.requires_previous);
  return (
    <div
      className={`group flex items-center gap-2 rounded-xl border bg-white p-2.5 shadow-sm transition-shadow dark:bg-navy-800 ${
        dragging ? "border-brand-400 shadow-lg" : "border-gray-200 dark:border-white/10"
      }`}
    >
      <button
        type="button"
        {...(dragHandleProps || {})}
        className="flex h-7 w-7 shrink-0 cursor-grab items-center justify-center rounded-md text-gray-300 opacity-60 transition-opacity hover:bg-gray-100 hover:text-gray-500 group-hover:opacity-100 focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 active:cursor-grabbing dark:text-gray-500 dark:hover:bg-white/10 dark:hover:text-gray-300"
        aria-label="Drag"
      >
        <MdDragIndicator className="h-4 w-4" />
      </button>
      {item.type === "image" && previewUrl && !thumbBroken ? (
        <div className="h-9 w-9 shrink-0 overflow-hidden rounded-lg ring-1 ring-black/5 dark:ring-white/10">
          <img
            src={previewUrl}
            alt=""
            className="h-9 w-9 object-cover"
            onError={() => setThumbBroken(true)}
            loading="lazy"
          />
        </div>
      ) : (
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${TONES[item.type]}`}>
          {ICONS[item.type]}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <InlineRename
          value={item.title}
          onSubmit={async (next) => {
            await onRename(item, next);
          }}
        >
          {({ edit, label }) => (
            <div className="flex min-w-0 items-center gap-1">
              <button
                type="button"
                onClick={() => onOpen?.(item)}
                className="block min-w-0 flex-1 truncate text-start text-sm font-semibold text-gray-800 dark:text-white"
                title={label}
              >
                {label}
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  edit();
                }}
                className="rounded-md p-1 text-gray-400 opacity-0 transition-opacity hover:bg-gray-100 hover:text-gray-700 group-hover:opacity-100 dark:hover:bg-white/10 dark:hover:text-white"
                aria-label="Edit"
                title="Edit"
              >
                <MdEdit className="h-4 w-4" />
              </button>
            </div>
          )}
        </InlineRename>
        <p className="text-[11px] uppercase tracking-wide text-gray-400">{item.type}</p>
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          void onToggleRequiresPrevious(item);
        }}
        className={`flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold transition-colors ${
          requiresPrevious
            ? "text-indigo-600 hover:bg-indigo-50 dark:text-indigo-300 dark:hover:bg-indigo-500/10"
            : "text-gray-400 hover:bg-gray-100 dark:hover:bg-white/10 dark:hover:text-gray-200"
        }`}
        aria-label={requiresPrevious ? "إلغاء شرط المحتوى السابق" : "تفعيل شرط المحتوى السابق"}
        title={
          requiresPrevious
            ? "يتطلب إكمال المحتوى السابق (أو اجتياز الاختبار بدرجة النجاح)"
            : "بدون شرط تسلسلي — متاح مباشرة"
        }
      >
        {requiresPrevious ? <MdLink className="h-4 w-4" /> : <MdLinkOff className="h-4 w-4" />}
        <span className="hidden sm:inline">{requiresPrevious ? "شرطي" : "حر"}</span>
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          void onToggleAccess(item);
        }}
        className={`flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold transition-colors ${
          isLocked
            ? "text-amber-500 hover:bg-amber-50 dark:text-amber-300 dark:hover:bg-amber-500/10"
            : "text-emerald-600 hover:bg-emerald-50 dark:text-emerald-300 dark:hover:bg-emerald-500/10"
        }`}
        aria-label={isLocked ? "فتح الملف للمشاهدة" : "قفل الملف للمشاهدة"}
        title={isLocked ? "مغلق للمشاهدة" : "مفتوح للمشاهدة"}
      >
        {isLocked ? <MdLock className="h-4 w-4" /> : <MdLockOpen className="h-4 w-4" />}
        <span className="hidden sm:inline">{isLocked ? "مغلق" : "مفتوح"}</span>
      </button>
      <button
        type="button"
        onClick={(e) => onOpenMenu(item, e.currentTarget)}
        className="rounded-md p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-white/10"
        aria-label="More"
      >
        <MdMoreVert className="h-5 w-5" />
      </button>
    </div>
  );
}
